'use client';

import { useLatestRef } from '@/hooks/ui/use-latest-ref';
import {
  type ReactNode,
  type ScriptHTMLAttributes,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const COLOR_THEMES = ['light', 'dark'] as const;
const DEFAULT_THEMES = [...COLOR_THEMES];
const SYSTEM_THEME_MEDIA = '(prefers-color-scheme: dark)';

type ThemeValue = string;
type ResolvedThemeValue = (typeof COLOR_THEMES)[number];

type ThemeProviderProps = {
  attribute?: string | string[];
  children: ReactNode;
  defaultTheme?: ThemeValue;
  disableTransitionOnChange?: boolean;
  enableColorScheme?: boolean;
  enableSystem?: boolean;
  forcedTheme?: ThemeValue;
  nonce?: string;
  scriptProps?: ScriptHTMLAttributes<HTMLScriptElement>;
  storageKey?: string;
  themes?: ThemeValue[];
  value?: Record<string, string>;
};

type SetThemeValue =
  | ThemeValue
  | ((previousTheme: ThemeValue | undefined) => ThemeValue);

type ThemeContextValue = {
  forcedTheme?: ThemeValue;
  resolvedTheme?: ThemeValue;
  setTheme: (value: SetThemeValue) => void;
  systemTheme?: ResolvedThemeValue;
  theme?: ThemeValue;
  themes: ThemeValue[];
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);
const FALLBACK_THEME_CONTEXT: ThemeContextValue = {
  setTheme: () => {},
  themes: DEFAULT_THEMES,
};

function isBrowser() {
  return typeof window !== 'undefined';
}

function getSystemTheme(mediaQueryList?: MediaQueryList): ResolvedThemeValue {
  if (!isBrowser()) {
    return 'light';
  }

  return (mediaQueryList ?? window.matchMedia(SYSTEM_THEME_MEDIA)).matches
    ? 'dark'
    : 'light';
}

function readStoredTheme(storageKey: string, fallbackTheme: ThemeValue) {
  if (!isBrowser()) {
    return fallbackTheme;
  }

  try {
    return window.localStorage.getItem(storageKey) ?? fallbackTheme;
  } catch {
    return fallbackTheme;
  }
}

function getResolvedTheme(params: {
  enableSystem: boolean;
  theme: ThemeValue;
}) {
  const { enableSystem, theme } = params;

  if (theme === 'system' && enableSystem) {
    return getSystemTheme();
  }

  return theme;
}

function applyThemeToDocument(params: {
  attribute: string | string[];
  defaultTheme: ThemeValue;
  enableColorScheme: boolean;
  resolvedTheme: ThemeValue;
  themes: ThemeValue[];
  value?: Record<string, string>;
}) {
  if (!isBrowser()) {
    return;
  }

  const {
    attribute,
    defaultTheme,
    enableColorScheme,
    resolvedTheme,
    themes,
    value,
  } = params;
  const root = document.documentElement;
  const attributes = Array.isArray(attribute) ? attribute : [attribute];
  const classThemeNames = value
    ? themes.map((themeName) => value[themeName] ?? themeName)
    : themes;
  const mappedTheme = value?.[resolvedTheme] ?? resolvedTheme;

  for (const attributeName of attributes) {
    if (attributeName === 'class') {
      root.classList.remove(...classThemeNames);
      if (mappedTheme) {
        root.classList.add(mappedTheme);
      }
      continue;
    }

    if (!mappedTheme) {
      root.removeAttribute(attributeName);
      continue;
    }

    root.setAttribute(attributeName, mappedTheme);
  }

  if (!enableColorScheme) {
    return;
  }

  const fallbackColorTheme = COLOR_THEMES.includes(
    defaultTheme as ResolvedThemeValue
  )
    ? (defaultTheme as ResolvedThemeValue)
    : undefined;
  const colorTheme = COLOR_THEMES.includes(resolvedTheme as ResolvedThemeValue)
    ? (resolvedTheme as ResolvedThemeValue)
    : fallbackColorTheme;

  if (colorTheme) {
    root.style.colorScheme = colorTheme;
  }
}

function disableTransitionsTemporarily(nonce?: string) {
  if (!isBrowser()) {
    return undefined;
  }

  const style = document.createElement('style');
  if (nonce) {
    style.setAttribute('nonce', nonce);
  }
  style.appendChild(
    document.createTextNode(
      '*{transition:none!important}*::before{transition:none!important}*::after{transition:none!important}'
    )
  );
  document.head.appendChild(style);

  return () => {
    window.getComputedStyle(document.body);
    window.setTimeout(() => {
      document.head.removeChild(style);
    }, 1);
  };
}

function buildThemeInitScript(params: {
  attribute: string | string[];
  defaultTheme: ThemeValue;
  enableColorScheme: boolean;
  enableSystem: boolean;
  forcedTheme?: ThemeValue;
  storageKey: string;
  themes: ThemeValue[];
  value?: Record<string, string>;
}) {
  const serializedArgs = JSON.stringify([
    params.attribute,
    params.storageKey,
    params.defaultTheme,
    params.forcedTheme ?? null,
    params.themes,
    params.value ?? null,
    params.enableSystem,
    params.enableColorScheme,
  ]);

  return `(function(attribute, storageKey, defaultTheme, forcedTheme, themes, value, enableSystem, enableColorScheme) {
  var colorThemes = ['light', 'dark'];
  var root = document.documentElement;
  function getSystemTheme() {
    return window.matchMedia('${SYSTEM_THEME_MEDIA}').matches ? 'dark' : 'light';
  }
  function resolveTheme(theme) {
    return theme === 'system' && enableSystem ? getSystemTheme() : theme;
  }
  function applyTheme(theme) {
    var resolvedTheme = resolveTheme(theme);
    var attributes = Array.isArray(attribute) ? attribute : [attribute];
    var classThemeNames = value
      ? themes.map(function(themeName) {
          return value[themeName] || themeName;
        })
      : themes;
    var mappedTheme = value && value[resolvedTheme] ? value[resolvedTheme] : resolvedTheme;
    for (var index = 0; index < attributes.length; index += 1) {
      var attributeName = attributes[index];
      if (attributeName === 'class') {
        root.classList.remove.apply(root.classList, classThemeNames);
        if (mappedTheme) {
          root.classList.add(mappedTheme);
        }
        continue;
      }
      if (!mappedTheme) {
        root.removeAttribute(attributeName);
        continue;
      }
      root.setAttribute(attributeName, mappedTheme);
    }
    if (!enableColorScheme) {
      return;
    }
    var fallbackColorTheme = colorThemes.indexOf(defaultTheme) >= 0 ? defaultTheme : null;
    var colorTheme = colorThemes.indexOf(resolvedTheme) >= 0 ? resolvedTheme : fallbackColorTheme;
    if (colorTheme) {
      root.style.colorScheme = colorTheme;
    }
  }
  if (forcedTheme) {
    applyTheme(forcedTheme);
    return;
  }
  try {
    var storedTheme = localStorage.getItem(storageKey) || defaultTheme;
    applyTheme(storedTheme);
  } catch (error) {
    applyTheme(defaultTheme);
  }
}).apply(null, ${serializedArgs});`;
}

function ThemeScript(props: {
  attribute: string | string[];
  defaultTheme: ThemeValue;
  enableColorScheme: boolean;
  enableSystem: boolean;
  forcedTheme?: ThemeValue;
  nonce?: string;
  scriptProps?: ScriptHTMLAttributes<HTMLScriptElement>;
  storageKey: string;
  themes: ThemeValue[];
  value?: Record<string, string>;
}) {
  const { nonce, scriptProps } = props;

  return (
    <script
      {...scriptProps}
      suppressHydrationWarning={true}
      nonce={typeof window === 'undefined' ? nonce : ''}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: Theme init script is generated in-app and protected with a nonce.
      dangerouslySetInnerHTML={{
        __html: buildThemeInitScript(props),
      }}
    />
  );
}

function subscribeToSystemThemeChanges(
  listener: (theme: ResolvedThemeValue) => void
) {
  if (!isBrowser()) {
    return () => {};
  }

  const mediaQueryList = window.matchMedia(SYSTEM_THEME_MEDIA);
  const handleChange = () => {
    listener(getSystemTheme(mediaQueryList));
  };

  if (typeof mediaQueryList.addEventListener === 'function') {
    mediaQueryList.addEventListener('change', handleChange);
    return () => {
      mediaQueryList.removeEventListener('change', handleChange);
    };
  }

  mediaQueryList.addListener(handleChange);
  return () => {
    mediaQueryList.removeListener(handleChange);
  };
}

export function ThemeProvider({
  attribute = 'data-theme',
  children,
  defaultTheme,
  disableTransitionOnChange = false,
  enableColorScheme = true,
  enableSystem = true,
  forcedTheme,
  nonce,
  scriptProps,
  storageKey = 'theme',
  themes = DEFAULT_THEMES,
  value,
}: ThemeProviderProps) {
  const resolvedDefaultTheme =
    defaultTheme ?? (enableSystem ? 'system' : 'light');
  const [theme, setThemeState] = useState<ThemeValue>(() =>
    readStoredTheme(storageKey, resolvedDefaultTheme)
  );
  const [systemTheme, setSystemTheme] = useState<ResolvedThemeValue>(() =>
    getSystemTheme()
  );

  const applyTheme = useCallback(
    (themeValue: ThemeValue) => {
      const cleanup = disableTransitionOnChange
        ? disableTransitionsTemporarily(nonce)
        : undefined;

      applyThemeToDocument({
        attribute,
        defaultTheme: resolvedDefaultTheme,
        enableColorScheme,
        resolvedTheme: forcedTheme
          ? getResolvedTheme({
              enableSystem,
              theme: forcedTheme,
            })
          : getResolvedTheme({
              enableSystem,
              theme: themeValue,
            }),
        themes,
        value,
      });

      cleanup?.();
    },
    [
      attribute,
      disableTransitionOnChange,
      enableColorScheme,
      enableSystem,
      forcedTheme,
      nonce,
      resolvedDefaultTheme,
      themes,
      value,
    ]
  );

  const setTheme = useCallback(
    (nextTheme: SetThemeValue) => {
      setThemeState((currentTheme) => {
        const resolvedTheme =
          typeof nextTheme === 'function' ? nextTheme(currentTheme) : nextTheme;

        try {
          window.localStorage.setItem(storageKey, resolvedTheme);
        } catch {
          // Ignore storage failures and keep the in-memory theme.
        }

        return resolvedTheme;
      });
    },
    [storageKey]
  );
  const themeSubscriptionStateRef = useLatestRef({
    applyTheme,
    forcedTheme,
    resolvedDefaultTheme,
    theme,
  });

  useEffect(() => {
    return subscribeToSystemThemeChanges((nextSystemTheme) => {
      const currentState = themeSubscriptionStateRef.current;
      setSystemTheme(nextSystemTheme);

      if (
        (currentState.theme ?? currentState.resolvedDefaultTheme) ===
          'system' &&
        !currentState.forcedTheme
      ) {
        currentState.applyTheme('system');
      }
    });
  }, [themeSubscriptionStateRef]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== storageKey) {
        return;
      }

      setThemeState(event.newValue ?? resolvedDefaultTheme);
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, [resolvedDefaultTheme, storageKey]);

  useEffect(() => {
    applyTheme(forcedTheme ?? theme ?? resolvedDefaultTheme);
  }, [applyTheme, forcedTheme, resolvedDefaultTheme, theme]);

  const contextValue = useMemo<ThemeContextValue>(
    () => ({
      forcedTheme,
      resolvedTheme:
        (forcedTheme ?? theme) === 'system' && enableSystem
          ? systemTheme
          : (forcedTheme ?? theme),
      setTheme,
      systemTheme: enableSystem ? systemTheme : undefined,
      theme,
      themes: enableSystem ? [...themes, 'system'] : themes,
    }),
    [enableSystem, forcedTheme, setTheme, systemTheme, theme, themes]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <ThemeScript
        attribute={attribute}
        defaultTheme={resolvedDefaultTheme}
        enableColorScheme={enableColorScheme}
        enableSystem={enableSystem}
        forcedTheme={forcedTheme}
        nonce={nonce}
        scriptProps={scriptProps}
        storageKey={storageKey}
        themes={themes}
        value={value}
      />
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext) ?? FALLBACK_THEME_CONTEXT;
}
